<script lang="ts">
    
    import { fade } from 'svelte/transition';
    import type { LngLatBoundsLike } from "mapbox-gl";
    import { Input, Label, Offcanvas } from "sveltestrap";
    import ClientMarker from "../Components/ClientMarker.svelte";
    import Map from "../Components/Map.svelte";
    import MapRoute from "../Components/MapRoute.svelte";
    import Marker from "../Components/Marker.svelte";
    import RoutesList from "../Components/RoutesList.svelte";
    import TopBar from "../Components/TopBar.svelte";
    import type { LonLat, Route } from "../global";
    import { routes } from "../Routes";
    import { geolocationService } from "../Services";
    import { getRouteBounds } from "../Services/Functions";
    import { tick } from 'svelte';
    import Compass from '../Components/Compass.svelte';
    import RouteSlide from '../Components/RouteSlide.svelte';
    import Slider from '../Components/Slider.svelte';
    import Slide from '../Components/Slide.svelte';
    import { navigate } from 'svelte-navigator';

    export let routeId: string = '';

    const activeRouteColor = '#104994';
    const secondaryRouteColor = '#12B1A8';

    let map: Map;   
    let slider: Slider;
    let routesArray = [...routes.values()];
    let bounds: LngLatBoundsLike = getRouteBounds(...routes.values());
    let route: Route;
    let otherRoutes: Route[] = [];
    let openRightMenu = false;
    let clientLonLat: LonLat = undefined;
    let walkRight = true;
    let satelliteMode = localStorage.getItem('satelliteMode') === '1';
    let routeIsFitted = true;
    let clientIsCentered = false;
    let bearing = 0;

    $: setupRoute(routeId);
    $: if(map) {
        const moved = () => {
            routeIsFitted = false;
            clientIsCentered = false;
        };
        map.map.on('dragstart', moved);
        map.map.on('zoomstart', moved);
        map.map.on('rotate', () => bearing = map.map.getBearing() * -1);
    }
    $: if (map && route) {
        tick().then(fitCurrentRoute);
    }
    $: updateClientMarker($geolocationService);
    $: localStorage.setItem('satelliteMode', satelliteMode ? '1' : '0');

    function updateClientMarker(postion: GeolocationPosition) {
        if (!postion) {
            return;
        }
        const { latitude, longitude } = postion.coords;
        walkRight = !clientLonLat || clientLonLat[0] < longitude;
        clientLonLat = [longitude, latitude];
        clientIsCentered = false;
    }

    function setupRoute(routeId: string): void {
        if (!routes.has(routeId)) {
            route = undefined;
            return;
        }
        route = routes.get(routeId);
        otherRoutes = [...routes.values()].filter(({ id }) => id !== route.id);
        openRightMenu = false;
    }

    function toggleRightMenu() {
        openRightMenu = !openRightMenu;
    }

    function toggleSatelliteMode() {
        satelliteMode = !satelliteMode;
    }

    function fitCurrentRoute() {
        if (!route) {
            return;
        }
        map.map.resize();
        map.fitBounds(getRouteBounds(route));
        routeIsFitted = true;
    }

    function resetBearing() {
        if (!map) {
            return;
        }
        map.map?.easeTo({
            bearing: 0,
            pitch: 0
        });
    }

    function onSliderChange({ detail: route }: CustomEvent<Route>): void {
        navigate(`/mapa/${route.id}`);
    }

    function centerClient() {
        if (!clientLonLat || !map) {
            return;
        }
        map.map?.easeTo({ center: clientLonLat });
        clientIsCentered = true;
        routeIsFitted = false;
    }

</script>

<div class="view" transition:fade>
    <TopBar backUrl="/menu">
        <button slot="right" type="button" on:click={toggleRightMenu}>
            <img src="/images/menu.svg" alt="">
        </button>
    </TopBar>
    <div class="map-container">

        <div class="compass-container">
            <Compass {bearing} on:click={resetBearing} />
        </div>

        <Map bind:this={map} {bounds} satellite={satelliteMode}>
            
            {#key route}
                {#if route}
                    <MapRoute {route} color={activeRouteColor} isActive={true} />
                {/if}
                {#each otherRoutes as route}
                    <MapRoute {route} color={secondaryRouteColor} />
                {/each}
            {/key}

            {#if clientLonLat}
                <Marker coordinates={clientLonLat} zIndex={10}>
                    <ClientMarker right={walkRight} />
                </Marker>
            {/if}

        </Map>
    </div>
    <div class="map-footer">
        <div class="routes-slider">
            <button type="button" on:click={() => slider?.prev()}>
                <img src="/images/arrow.svg" alt="<">
            </button>
            <div class="slider">
                <Slider bind:this={slider} slideTo={route} spaceBetween={5} on:change={onSliderChange}>
                    {#each [...routesArray] as route}
                        <Slide index={route}>
                            <RouteSlide {route} />
                        </Slide>
                    {/each}
                </Slider>
            </div>
            <button type="button" on:click={() => slider?.next()}>
                <img src="/images/arrow.svg" alt="<" style="transform: scaleX(-1);">
            </button>
        </div>
        <div class="map-control">
            <div class="d-flex align-items-center">
                <Input id="satellitw-switch" type="switch" checked={satelliteMode} on:change={toggleSatelliteMode} />
                <Label for="satellitw-switch" class="m-0">Modo satelite</Label>
            </div>
            <div class="d-flex gap-2">
                <button
                    type="button"
                    class:active={!routeIsFitted}
                    on:click={fitCurrentRoute}>
                   <img src="/images/refresh.svg" alt="" style="--size: 20px">
                </button>
                <button
                    type="button"
                    class:active={!clientIsCentered}
                    on:click={centerClient}>
                   <img src="/images/crosshair.svg" alt="">
                </button>
            </div>
        </div>
    </div>
</div>

<Offcanvas header="Rutas" isOpen={openRightMenu} toggle={toggleRightMenu} placement="end" color="red">
    <RoutesList current={routeId} />
</Offcanvas>

<style lang="scss">

    @use '../variables';

    .view {
        display: flex;
        flex-direction: column;
    }

    .map-container {
        flex: 1 1 0;
        position: relative;
    }

    .compass-container {
        position: absolute;
        z-index: 11;
        top: 1rem;
        right: 1rem;
    }

    .map-footer {
        position: relative;
        z-index: 3;
        color: #fff;
        background-color: variables.$color-background;
    }

    .routes-slider {
        padding: 1rem;
        box-sizing: border-box;
        height: 78px;
        display: flex;

        .slider {
            width: calc(100% - 60px);
            margin-top: -37px;
        }

        button {
            border: none;
            background-color: transparent;
            padding: 0;
            width: 30px;
        }

        @media (min-width: 500px) {
            height: 94px;

            .slider {
                margin-top: -42px;
            }
        }
    }

    .map-control {
        display: flex;
        justify-content: space-evenly;
        align-items: center;
        padding: 1rem;
        background-color: variables.$color-secondary;

        button {
            $size: 45px;

            width: $size;
            height: $size;
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            border-radius: 0.25rem;
            border: none;
            background-color: variables.$color-primary;
            opacity: 0.5;

            &.active {
                opacity: 1;
            }

            img {
                width: var(--size, #{$size * 0.6});
                height: var(--size, #{$size * 0.6});
            }
        }
    }

</style>
